import { ClientTagFilter } from "../ClientTags/ClientTags";
import { canManageClientTags } from "intake_shared/src/client_tags";
import { PageHeader } from "../../bits/PageHeader/PageHeader";
import * as React from "react";
import queryString from "query-string";
import { useHistory, useLocation } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import {
    ClientStatus,
    IArchivedClient,
    IClient,
    IMasterListSearchByOptions,
    MasterListStatus,
} from "intake_shared/src/types";
import { handleFormatDatePickerValue } from "../../../../utils/utils";
import { uniqBy } from "lodash";
import { formatPhoneNumber } from "react-phone-number-input";
import store from "../../../../store";
import {
    MasterListPageCursor,
    MASTER_LIST_PAGE_SIZE,
} from "../../../../store/master_list";
import TablePagination from "../../bits/Table/TablePagination";
import ClientMasterList from "./ClientMasterList/ClientMasterList";
import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import styles from "./ClientMasterListWrapper.module.scss";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import FilterButton from "../../bits/FilterButton/FilterButton";
import PhoneInput from "../../bits/PhoneInput/PhoneInput";
import { DatePicker } from "../../bits/DatePicker/DatePicker";
import { Button } from "../../bits/Button/Button";
import SearchIcon from "../../icons/Search";
import { Input } from "../../bits/Input/Input";
import Loader from "../../bits/Loader/Loader";
import CompareMergeDialog from "./CompareMergeDialog/CompareMergeDialog";
import { Dialog } from "../../bits/Dialog/Dialog";
import CompleteClientFlow from "../client-forms/CompleteClientFlow/CompleteClientFlow";
import { AclContext } from "../../../../contexts/AclContext";
import {
    getClientMasterListNavigationState,
    getRestoredDate,
    serializeClientMasterListSearchOptions,
} from "./client_master_list_state";

export default function ClientMasterListWrapper() {
    const { addToast } = useToasts();
    const aclContext = React.useContext(AclContext);
    const browserLocation = useLocation();
    const history = useHistory();
    const parsedQueryParams = queryString.parse(browserLocation.search || "");
    const navigationState = getClientMasterListNavigationState(
        browserLocation.state
    );
    const restoredListState = navigationState.clientMasterList;
    const [filterByStatus, setFilterByStatus] = React.useState(
        restoredListState?.filterByStatus ||
            ((parsedQueryParams["status"] || "ALL") as MasterListStatus)
    );
    const [loading, setLoading] = React.useState(false);
    const [showCompareMergeDialog, setShowCompareMergeDialog] =
        React.useState(false);
    const [selectedClientsForMerge, setSelectedClientsForMerge] = React.useState<
        (IClient | IArchivedClient)[]
    >([]);
    const [primaryClient, setPrimaryClient] = React.useState<
        IClient | IArchivedClient | null
    >(null);
    const [showCloseClientDialog, setShowCloseClientDialog] = React.useState(false);
    const [selectedClientsToClose, setSelectedClientsToClose] = React.useState<
        IClient[]
    >([]);
    const [searchByOptions, setSearchByOptions] =
        React.useState<IMasterListSearchByOptions>({
            tagKeys: restoredListState?.searchByOptions.tagKeys ?? [],
            email: restoredListState?.searchByOptions.email || "",
            lastname: restoredListState?.searchByOptions.lastname || "",
            firstname: restoredListState?.searchByOptions.firstname || "",
            phoneno: restoredListState?.searchByOptions.phoneno || "",
            date_of_birth: getRestoredDate(
                restoredListState?.searchByOptions.date_of_birth
            ),
        });

    const [clients, setClients] = React.useState<(IClient | IArchivedClient)[]>([]);
    const searchRequest = React.useRef(0);
    const tagSearchTimeout = React.useRef<ReturnType<typeof setTimeout>>();
    const [filterLoading, setFilterLoading] = React.useState(false);
    const [pagination, setPagination] = React.useState<{
        page: number;
        cursors: Array<MasterListPageCursor | undefined>;
        total?: number;
        hasNext: boolean;
        status: MasterListStatus;
        options: IMasterListSearchByOptions;
    }>({
        page: 1,
        cursors: [undefined],
        hasNext: false,
        status: filterByStatus,
        options: searchByOptions,
    });

    React.useEffect(
        () => () => {
            clearTimeout(tagSearchTimeout.current);
            searchRequest.current += 1;
        },
        []
    );
    const shouldRestoreSearch = React.useRef(
        restoredListState?.hasSearched === true
    );

    const searchingForUSER = Object.values(searchByOptions).some(
        (option) => !Array.isArray(option) && option !== "" && option !== null
    );

    const doAllClientsHaveSameEmail = () => {
        if (clients?.length > 1) {
            const firstClient = clients?.[0];
            return clients?.every((client) => client.email === firstClient.email);
        }
        return false;
    };

    const getStatusLabel = (status: MasterListStatus) => {
        if (status === "ALL") return "All clients";
        if (status === "active") return "Active clients";
        if (status === "inactive") return "Inactive clients";
        return status;
    };

    const searchList = React.useCallback(
        async (
            options = searchByOptions,
            page = 1,
            status = filterByStatus,
            paging = false
        ) => {
            const requestId = ++searchRequest.current;
            const normalizedOptions = {
                ...options,
                phoneno: formatPhoneNumber(options.phoneno),
            };
            const paged = options.tagKeys?.length
                ? await store.searchMasterListPage(
                      status,
                      normalizedOptions,
                      paging ? pagination.cursors[page - 1] : undefined,
                      paging ? pagination.total : undefined
                  )
                : undefined;
            const results = paged
                ? paged.clients
                : await store.searchMasterList(status, normalizedOptions);
            if (requestId !== searchRequest.current) return false;
            const dedupedResults = uniqBy(results, "id");
            setClients(dedupedResults);
            setPagination({
                page,
                cursors: paged
                    ? [
                          ...(paging
                              ? pagination.cursors.slice(0, page)
                              : [undefined]),
                          paged.nextCursor,
                      ]
                    : [undefined],
                total: paged?.total,
                hasNext: Boolean(paged?.nextCursor),
                status,
                options,
            });
            if (dedupedResults.length === 0) {
                addToast("No clients found", {
                    appearance: "error",
                });
            }
            return true;
        },
        [addToast, filterByStatus, searchByOptions, pagination]
    );

    React.useEffect(() => {
        if (!shouldRestoreSearch.current) {
            return;
        }

        shouldRestoreSearch.current = false;
        const restoreSearch = async () => {
            setLoading(true);
            try {
                await searchList();
            } catch {
                addToast("Unable to restore client search. Please try again.", {
                    appearance: "error",
                });
            } finally {
                setLoading(false);
            }
        };

        restoreSearch();
    }, [addToast, searchList]);

    const applySearch = async (
        options = searchByOptions,
        background = false,
        page = 1,
        status = filterByStatus,
        paging = false
    ) => {
        if (background) setFilterLoading(true);
        else setLoading(true);
        const request = searchList(options, page, status, paging);
        const requestId = searchRequest.current;
        try {
            if (!(await request)) return;
            history.replace({
                pathname: browserLocation.pathname,
                search: browserLocation.search,
                state: {
                    ...navigationState,
                    clientMasterList: {
                        filterByStatus: status,
                        searchByOptions:
                            serializeClientMasterListSearchOptions(options),
                        hasSearched: true,
                    },
                },
            });
        } catch {
            if (requestId === searchRequest.current) {
                addToast("Unable to search clients. Please try again.", {
                    appearance: "error",
                });
            }
        } finally {
            if (requestId === searchRequest.current) {
                setLoading(false);
                setFilterLoading(false);
            }
        }
    };

    function mergeCheck(client, parent) {
        if (parent) {
            const clientFirstName = client?.first_name?.trim()?.toLowerCase();
            const parentFirstName = parent?.first_name?.trim()?.toLowerCase();

            const isFirstNameMatch = clientFirstName === parentFirstName;

            if (!isFirstNameMatch) {
                return false;
            }

            if (parent && client?.status !== ClientStatus.INCOMPLETE_INQUIRY) {
                const clientDateOfBirth = `${handleFormatDatePickerValue(
                    client?.date_of_birth
                )}T00:00:00.000`;
                const parentDateOfBirth = `${handleFormatDatePickerValue(
                    parent?.date_of_birth
                )}T00:00:00.000`;

                return clientDateOfBirth === parentDateOfBirth;
            }

            return true;
        }
        return true;
    }

    const handleMergeClick = (selectedClients: IClient[]) => {
        if (selectedClients.length < 2) {
            addToast("Please select at least 2 clients to merge.", {
                appearance: "error",
                autoDismiss: false,
            });
            return;
        }

        const eligiblePrimary = selectedClients.find(
            (item) =>
                item.status !== ClientStatus.INCOMPLETE_INQUIRY &&
                item.status !== ClientStatus.COMPLETED
        );

        setPrimaryClient(eligiblePrimary ?? null);

        setSelectedClientsForMerge(selectedClients);

        if (eligiblePrimary) {
            const hasSameFirstNameAndDateOfBirth = selectedClients.every((client) =>
                mergeCheck(client, eligiblePrimary)
            );
            if (!hasSameFirstNameAndDateOfBirth) {
                addToast(
                    `Cannot merge charts because one or more of the following fields differ between the client charts: first name, email, or date of birth. We have treated them as different clients.
      If you are certain they refer to the same client, please update these fields to match before attempting to merge again.`,
                    {
                        appearance: "error",
                    }
                );
                return;
            }
        }

        const allCompleted = selectedClients.every(
            (client) => client.status === ClientStatus.COMPLETED
        );
        if (allCompleted) {
            addToast(
                `Cannot merge clients because all selected clients have Completed status. Completed clients cannot be merged.`,
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
            return;
        }

        setShowCompareMergeDialog(true);
    };

    const handleMergeWithSelection = async (
        primaryClientId: string,
        selectedFields: {
            [clientId: string]: string[];
        }
    ) => {
        setLoading(true);
        try {
            if (!primaryClientId) {
                addToast(
                    "Please select a primary client and at least one field to merge.",
                    {
                        appearance: "error",
                    }
                );
                return;
            }

            const primaryClient = selectedClientsForMerge.find(
                (client) => client.id === primaryClientId
            );
            const otherClients = selectedClientsForMerge.filter(
                (client) => client.id !== primaryClientId
            );

            if (!primaryClient) {
                addToast("Primary client not found.", {
                    appearance: "error",
                });
                return;
            }

            const result = await store.mergeClientFromArchive({
                clientId: primaryClient.id,
                sourceIds: otherClients.map((client) => client.id),
                selectedFields,
            });

            if (result) {
                await searchList();
                addToast("Clients merged successfully", {
                    appearance: "success",
                });
            }
            setShowCompareMergeDialog(false);
        } catch (error) {
            addToast(`Error merging clients`, {
                appearance: "error",
            });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const incompleteInquiryClient = selectedClientsForMerge?.find(
        (client) => client?.status === ClientStatus.INCOMPLETE_INQUIRY
    );

    const handleMarkAsSpam = (selectedClients: IClient[]) => {
        if (selectedClients.length === 0) {
            addToast("Please select at least one client to close.", {
                appearance: "error",
            });
            return;
        }

        if (
            selectedClients.some(
                (client) => client.status === ClientStatus.INS_VERIFICATION
            )
        ) {
            addToast(
                "Cannot close clients because one or more of the selected clients is in verification.",
                {
                    appearance: "error",
                }
            );
            return;
        }

        // Check if any selected clients are already completed
        const hasCompletedClients = selectedClients.some(
            (client) => client.status === ClientStatus.COMPLETED
        );
        if (hasCompletedClients) {
            addToast(
                "Cannot close clients because one or more of the selected clients is already closed.",
                {
                    appearance: "error",
                    autoDismiss: false,
                }
            );
            return;
        }

        setSelectedClientsToClose(selectedClients);
        setShowCloseClientDialog(true);
    };

    const handleCloseComplete = async (client?: IClient, close?: boolean) => {
        if (close === false) {
            return;
        } else {
            setLoading(true);
            try {
                await searchList();
            } finally {
                setLoading(false);
            }
            setShowCloseClientDialog(false);
            setSelectedClientsToClose([]);
        }
    };

    return (
        <div className="v2-page">
            <Loader show={loading} />
            <PageHeader title="Client Master List" />
            <div className={styles.filters}>
                <div className={styles.filterTop}>
                    <Dropdown>
                        <Dropdown.Trigger>
                            <FilterButton text={getStatusLabel(filterByStatus)} />
                        </Dropdown.Trigger>
                        <Dropdown.Content className={styles.filterTop_dropdown}>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={filterByStatus === "ALL"}
                                onClick={() => {
                                    setFilterByStatus("ALL");
                                }}
                            >
                                Show all
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={filterByStatus === "active"}
                                onClick={() => {
                                    setFilterByStatus("active");
                                }}
                            >
                                Active clients
                            </Dropdown.Item>
                            <Dropdown.Item
                                closeMenuOnSelect
                                isSelected={filterByStatus === "inactive"}
                                onClick={() => {
                                    setFilterByStatus("inactive");
                                }}
                            >
                                Inactive clients
                            </Dropdown.Item>
                        </Dropdown.Content>
                    </Dropdown>
                    {canManageClientTags(aclContext?.authUser?.acl) && (
                        <ClientTagFilter
                            value={searchByOptions.tagKeys ?? []}
                            onChange={(tagKeys) => {
                                const options = { ...searchByOptions, tagKeys };
                                setSearchByOptions(options);
                                clearTimeout(tagSearchTimeout.current);
                                searchRequest.current += 1;
                                setFilterLoading(true);
                                tagSearchTimeout.current = setTimeout(() => {
                                    void applySearch(options, true);
                                }, 500);
                            }}
                        />
                    )}
                    <form
                        className={styles.filterTop_search}
                        onSubmit={(event) => {
                            event.preventDefault();
                            clearTimeout(tagSearchTimeout.current);
                            void applySearch();
                        }}
                    >
                        <div className={styles.filterTop_flex}>
                            <Input
                                placeholder="Email"
                                value={searchByOptions.email}
                                onChange={(e) => {
                                    setSearchByOptions({
                                        ...searchByOptions,
                                        email: e.target.value,
                                    });
                                }}
                                type="email"
                                className={styles.filterTop_input}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <Input
                                placeholder="First name"
                                value={searchByOptions.firstname}
                                onChange={(e) => {
                                    setSearchByOptions({
                                        ...searchByOptions,
                                        firstname: e.target.value,
                                    });
                                }}
                                className={styles.filterTop_input}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <Input
                                placeholder="Last name"
                                value={searchByOptions.lastname}
                                onChange={(e) => {
                                    setSearchByOptions({
                                        ...searchByOptions,
                                        lastname: e.target.value,
                                    });
                                }}
                                className={styles.filterTop_input}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <PhoneInput
                                placeholder="Phone no"
                                className={styles.filterTop_input}
                                value={searchByOptions.phoneno}
                                onChange={(phoneNo: string | undefined) => {
                                    setSearchByOptions({
                                        ...searchByOptions,
                                        phoneno: phoneNo || "",
                                    });
                                }}
                            />
                        </div>
                        <div className={styles.filterTop_flex}>
                            <DatePicker
                                dateFormat={"MM/dd/yyyy"}
                                selected={searchByOptions.date_of_birth}
                                onChange={(date: Date) => {
                                    setSearchByOptions({
                                        ...searchByOptions,
                                        date_of_birth: date,
                                    });
                                }}
                                placeholderText="Date of birth"
                                inputSize="small"
                            />
                        </div>
                        <Button
                            type="submit"
                            size="icon"
                            title="Search"
                            aria-label="Search"
                        >
                            <SearchIcon />
                        </Button>
                    </form>
                </div>
                <div className={styles.filterBottom}>
                    {filterLoading && <span role="status">Updating results…</span>}
                    {(clients.length > 0 || pagination.total !== undefined) && (
                        <div className={styles.clientsCount}>
                            {pagination.total !== undefined
                                ? `${pagination.total} matching client(s)`
                                : `Showing ${clients?.length} ${
                                      filterByStatus === "ALL" ? "" : filterByStatus
                                  } client(s)`}
                        </div>
                    )}
                </div>
            </div>
            {clients.length > 0 ? (
                <ClientMasterList
                    key={searchRequest.current}
                    clients={clients}
                    allowSelect={true}
                    onCompletionReasonSaved={
                        aclContext?.authUser.acl?.intake ||
                        aclContext?.authUser.acl?.super_admin
                            ? (clientId, reason) =>
                                  setClients((currentClients) =>
                                      currentClients.map((client) =>
                                          client.id === clientId
                                              ? {
                                                    ...client,
                                                    complete_notes: {
                                                        ...client.complete_notes,
                                                        reason,
                                                    },
                                                }
                                              : client
                                      )
                                  )
                            : undefined
                    }
                    onSelectActions={(rows: IClient[]) => [
                        <Dropdown.Item
                            key="mark-as-spam"
                            onClick={() => handleMarkAsSpam(rows)}
                        >
                            Close client
                        </Dropdown.Item>,
                        ...(searchingForUSER && doAllClientsHaveSameEmail()
                            ? [
                                  <Dropdown.Item
                                      key="merge-client"
                                      onClick={() => handleMergeClick(rows)}
                                  >
                                      Merge Client
                                  </Dropdown.Item>,
                              ]
                            : []),
                    ]}
                />
            ) : (
                <EmptyListState
                    emptyMessage={
                        restoredListState?.hasSearched
                            ? "No clients found"
                            : "Search for a client"
                    }
                    stateHelperText={
                        restoredListState?.hasSearched
                            ? "Try a different name, email, phone number or date of birth."
                            : "Enter a name, email, phone number or date of birth above to see matching clients."
                    }
                />
            )}

            {pagination.total !== undefined && pagination.total > 0 && (
                <TablePagination
                    currentPage={pagination.page}
                    onFirstPage={() => {
                        if (!filterLoading && !loading)
                            void applySearch(
                                pagination.options,
                                true,
                                1,
                                pagination.status,
                                true
                            );
                    }}
                    onPrevPage={() => {
                        if (!filterLoading && !loading)
                            void applySearch(
                                pagination.options,
                                true,
                                pagination.page - 1,
                                pagination.status,
                                true
                            );
                    }}
                    onNextPage={() => {
                        if (!filterLoading && !loading)
                            void applySearch(
                                pagination.options,
                                true,
                                pagination.page + 1,
                                pagination.status,
                                true
                            );
                    }}
                    isNextDisabled={!pagination.hasNext || filterLoading || loading}
                    pageNumberText={`Page ${pagination.page} of ${Math.max(
                        1,
                        Math.ceil(pagination.total / MASTER_LIST_PAGE_SIZE)
                    )}`}
                />
            )}
            <CompareMergeDialog
                isOpen={showCompareMergeDialog}
                onClose={() => setShowCompareMergeDialog(false)}
                onMerge={handleMergeWithSelection}
                selectedClients={selectedClientsForMerge}
                loading={loading}
                defaultPrimaryClient={
                    primaryClient ||
                    incompleteInquiryClient ||
                    selectedClientsForMerge[0]
                }
            />
            {showCloseClientDialog && selectedClientsToClose.length > 0 && (
                <Dialog
                    open={showCloseClientDialog}
                    onClose={() => {
                        setShowCloseClientDialog(false);
                        setSelectedClientsToClose([]);
                    }}
                    title={`Close ${selectedClientsToClose.length} ${
                        selectedClientsToClose.length === 1 ? "client" : "clients"
                    }`}
                    showFooter={false}
                >
                    <CompleteClientFlow
                        title={`Close ${selectedClientsToClose.length} ${
                            selectedClientsToClose.length === 1
                                ? "client"
                                : "clients"
                        }`}
                        clientProp={
                            selectedClientsToClose.find(
                                (c) => c.status !== ClientStatus.COMPLETED
                            ) || selectedClientsToClose[0]
                        }
                        expectedCaller="admin"
                        showCompletionReason
                        showCompletionNotes
                        onSaveClient={handleCloseComplete}
                        hideCard
                        disableInternalDialogs={false}
                        completeBtnText={`Close ${selectedClientsToClose.length} ${
                            selectedClientsToClose.length === 1
                                ? "client"
                                : "clients"
                        }`}
                        isBulkOperation={selectedClientsToClose.length > 1}
                        bulkClients={selectedClientsToClose}
                        onBulkComplete={handleCloseComplete}
                    />
                </Dialog>
            )}
        </div>
    );
}
